import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { centsToDollarString, formatCents } from "./zipSlotAdsHelpers";
import { MIN_BID_CENTS } from "./zipSlotAdsConstants";

const PlaceBidDialog = ({
  open,
  onClose,
  zip,
  minBidCents,
  currentOwnBidCents = null,
  isUpdate = false,
  submitting = false,
  error = "",
  onSubmit,
}) => {
  const minDollars = (minBidCents || MIN_BID_CENTS) / 100;
  const defaultAmount = useMemo(() => {
    if (currentOwnBidCents != null) {
      return centsToDollarString(currentOwnBidCents);
    }
    return minDollars.toFixed(2);
  }, [currentOwnBidCents, minDollars]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { bidAmount: defaultAmount },
    mode: "onChange",
  });

  useEffect(() => {
    if (open) {
      reset({ bidAmount: defaultAmount });
    }
  }, [open, defaultAmount, reset]);

  const submit = handleSubmit((data) => {
    onSubmit(data.bidAmount);
  });

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      PaperProps={{ sx: { minWidth: { xs: 280, sm: 480 } } }}
    >
      <form onSubmit={submit}>
        <DialogTitle>
          {isUpdate ? "Update bid" : "Place bid"} for ZIP {zip}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ pb: 2 }}>
            Enter your cost-per-click bid. The minimum is {formatCents(minBidCents)}.
            {minBidCents > MIN_BID_CENTS
              ? ` All 7 slots are filled, so this bid must beat the current #7 slot by $10.00.`
              : ""}
          </DialogContentText>

          <Controller
            name="bidAmount"
            control={control}
            rules={{
              required: "Bid amount is required",
              validate: (value) => {
                const n = Number(value);
                if (!Number.isFinite(n)) return "Enter a valid number";
                if (n < minDollars) {
                  return `Your bid must be at least ${formatCents(minBidCents)}`;
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Bid per click"
                type="number"
                fullWidth
                inputProps={{ min: minDollars, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                disabled={submitting}
                error={!!errors.bidAmount}
                helperText={errors.bidAmount?.message}
              />
            )}
          />

          {error ? (
            <FormHelperText error sx={{ mt: 1 }}>
              {error}
            </FormHelperText>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={submitting}
            disabled={submitting || Object.keys(errors).length > 0}
          >
            {isUpdate ? "Update bid" : "Place bid"}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export function PaymentMethodStatus({ paymentMethod, loading }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", py: 1, mb: 2 }}>
        <CircularProgress size={20} sx={{ mr: 1.5 }} />
      </Box>
    );
  }
  if (!paymentMethod) return null;
  if (paymentMethod.success && paymentMethod.paymentMethod?.card) {
    const card = paymentMethod.paymentMethod.card;
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Card on file:{" "}
          <Box component="span" sx={{ textTransform: "capitalize" }}>
            {card.brand}
          </Box>{" "}
          ending in {card.last4}
        </Typography>
      </Alert>
    );
  }
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {paymentMethod.message ||
        "No payment method on file. A top-up will collect a card at checkout."}
    </Alert>
  );
}

export default PlaceBidDialog;
